import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import {
    APPLICATION_CHEMICALS_PATH,
    APPLICATION_CHEMICALS_SEED_PATH,
    APPLICATION_MIX_PATH,
    CHEMICAL_DB_API_HOST,
    JSON_HEADERS
} from './constants';
import type {
    ApplicationChemicalsResponse,
    AttachedMix,
    DetachApplicationChemicals,
    FetchApplicationChemicals,
    FetchApplicationMix,
    SeedApplicationChemicalsFromMix,
    UpdateApplicationChemicals
} from './types';

const CONNECTION_ERROR = 'Connection error';
const SAVE_FAILED = 'Failed to save application chemicals';

const useApplicationChemicals = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedDelete, authenticatedGet, authenticatedPost, authenticatedPut } = useFetch(
        { apiHost: CHEMICAL_DB_API_HOST, headers: JSON_HEADERS }
    );
    const { withRetry } = useRetry();

    const fetchApplicationChemicals: FetchApplicationChemicals = useCallback(
        async ({ applicationId }) => {
            try {
                setIsLoading(true);

                const response = await withRetry(
                    () =>
                        authenticatedGet<ApplicationChemicalsResponse>(
                            APPLICATION_CHEMICALS_PATH(applicationId)
                        ),
                    CONNECTION_ERROR,
                    async () => {
                        const retryResult = await fetchApplicationChemicals({ applicationId });

                        return retryResult ? { chemicals: retryResult } : null;
                    }
                );

                return response?.chemicals ?? null;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry]
    );

    const seedFromMix: SeedApplicationChemicalsFromMix = useCallback(
        async ({ applicationId, mixId }) => {
            try {
                setIsLoading(true);

                const response = await withRetry(
                    () =>
                        authenticatedPost<ApplicationChemicalsResponse>(
                            APPLICATION_CHEMICALS_SEED_PATH(applicationId),
                            JSON.stringify({ mixId })
                        ),
                    SAVE_FAILED,
                    async () => {
                        const retryResult = await seedFromMix({ applicationId, mixId });

                        return retryResult ? { chemicals: retryResult } : null;
                    }
                );

                return response?.chemicals ?? null;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPost, withRetry]
    );

    const updateApplicationChemicals: UpdateApplicationChemicals = useCallback(
        async ({ applicationId, chemicals }) => {
            try {
                setIsLoading(true);

                const response = await withRetry(
                    () =>
                        authenticatedPut<ApplicationChemicalsResponse>(
                            APPLICATION_CHEMICALS_PATH(applicationId),
                            JSON.stringify({ chemicals })
                        ),
                    SAVE_FAILED,
                    async () => {
                        const retryResult = await updateApplicationChemicals({
                            applicationId,
                            chemicals
                        });

                        return retryResult ? { chemicals: retryResult } : null;
                    }
                );

                return response?.chemicals ?? null;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPut, withRetry]
    );

    const detachApplicationChemicals: DetachApplicationChemicals = useCallback(
        async ({ applicationId }) => {
            try {
                setIsLoading(true);

                const result = await withRetry(
                    () => authenticatedDelete(APPLICATION_CHEMICALS_PATH(applicationId)),
                    SAVE_FAILED,
                    () => detachApplicationChemicals({ applicationId })
                );

                return result !== null;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedDelete, withRetry]
    );

    const fetchApplicationMix: FetchApplicationMix = useCallback(
        async ({ applicationId }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () => authenticatedGet<AttachedMix>(APPLICATION_MIX_PATH(applicationId)),
                    CONNECTION_ERROR,
                    () => fetchApplicationMix({ applicationId })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry]
    );

    return {
        detachApplicationChemicals,
        fetchApplicationChemicals,
        fetchApplicationMix,
        isLoading,
        seedFromMix,
        updateApplicationChemicals
    };
};

export default useApplicationChemicals;
